//@ts-nocheck
import backendInstance from 'lib/axios'
import { APP_MODES } from '@/main.lib'

// Refresh the token when it has less than this long left, to avoid using a
// token that expires mid-request.
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000 // 5 minutes
const INITIAL_BACKOFF_MS = 5_000 // 5 s — first retry delay after a failed refresh
const MAX_BACKOFF_MS = 60_000 // 60 s — ceiling for exponential backoff

function isTooManyRequestsError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as any
  return (
    e.name === 'TooManyRequestsException' ||
    e.__type === 'TooManyRequestsException' ||
    (typeof e.message === 'string' &&
      e.message.toLowerCase().includes('rate exceeded'))
  )
}

// Any NotAuthorizedException from fetchAuthSession() means the session is
// permanently dead (expired refresh token, revoked token, invalid token, etc.).
// Checking for specific message text is fragile — Cognito uses different messages
// for the same permanent failure: "has been revoked", "has expired", "not found".
// In this context (token refresh, not sign-in), all NotAuthorizedException errors
// are unrecoverable so we treat the whole error name as the signal.
function isPermanentSessionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as any
  return (
    e.name === 'NotAuthorizedException' || e.__type === 'NotAuthorizedException'
  )
}

class ApiService {
  private readonly backendInstance
  private amplify
  private readonly tokenGenerator: (force?: boolean) => Promise<string>
  // Cached token value and its expiry (ms since epoch).
  // forced: an early refresh already ran and could not extend the token
  // (for example a Unity SSO session), so keep serving it until expiry.
  private cachedToken: {
    value: string
    expiresAt: number
    forced?: boolean
  } | null = null
  private amplifySessionKey = 'none'
  // Deduplicates concurrent refresh calls — all callers share one in-flight fetch
  private pendingTokenFetch: Promise<string> | null = null
  private pendingIsForced = false
  // Exponential backoff state after a failed refresh (rate limit, network, 5xx)
  private rateLimitBackoffUntil = 0
  private rateLimitBackoffMs = INITIAL_BACKOFF_MS
  private backoffIsRateLimit = false
  // Set when the refresh token is permanently revoked — blocks all further
  // Cognito calls until the user signs in again (setAmplify resets this).
  private revokedTokenUntil = 0

  constructor(backendInstance) {
    this.backendInstance = backendInstance

    this.tokenGenerator = (force = false) => this.resolveToken(force)
  }

  private sessionKey(amplify) {
    if (!amplify) return 'none'
    const email = amplify.user?.email ?? ''
    return `${amplify.isSSO ? 'sso' : 'cognito'}:${email}`
  }

  private async readSession(force) {
    if (force && typeof this.amplify?.refreshToken === 'function') {
      return this.amplify.refreshToken()
    }
    const sessionFn =
      this.amplify?.fetchAuthSession ?? this.amplify?.fetchUserSessionInfo
    if (typeof sessionFn !== 'function') return null
    if (force) return sessionFn({ forceRefresh: true })
    return sessionFn()
  }

  private tokenPayload(tokenStr) {
    try {
      return JSON.parse(atob(tokenStr.split('.')[1])) ?? {}
    } catch {
      return {} // Not a JWT — callers fall back to a 50-minute validity
    }
  }

  private tokenExpiry(tokenStr) {
    const { exp } = this.tokenPayload(tokenStr)
    return typeof exp === 'number' ? exp * 1000 : Date.now() + 50 * 60 * 1000
  }

  // A just-issued token is timed by its own lifetime (exp − iat), so a client
  // clock that runs far ahead cannot make every refreshed token look expired.
  private freshTokenExpiry(tokenStr) {
    const { exp, iat } = this.tokenPayload(tokenStr)
    if (typeof exp === 'number' && typeof iat === 'number' && exp > iat) {
      return Date.now() + (exp - iat) * 1000
    }
    return this.tokenExpiry(tokenStr)
  }

  private needsRefresh(cached) {
    const remaining = cached.expiresAt - Date.now()
    if (remaining > TOKEN_REFRESH_BUFFER_MS) return false
    return !(cached.forced && remaining > 0)
  }

  private async forceRefresh() {
    const previous = this.cachedToken?.value ?? null
    const session = await this.readSession(true)
    const tokenStr = session?.tokens?.idToken?.toString()
    if (!tokenStr) {
      this.cachedToken = null
      return tokenStr
    }
    const expiresAt =
      tokenStr === previous
        ? this.tokenExpiry(tokenStr)
        : this.freshTokenExpiry(tokenStr)
    this.cachedToken = {
      value: tokenStr,
      expiresAt,
      forced:
        tokenStr === previous ||
        expiresAt - Date.now() <= TOKEN_REFRESH_BUFFER_MS,
    }
    this.rateLimitBackoffMs = INITIAL_BACKOFF_MS
    return tokenStr
  }

  // Reads the stored session on every call, as Amplify does, so a sign-out or
  // a refresh in another tab is seen at once. Refreshes early inside the window.
  private async fetchToken(force) {
    if (!force) {
      const session = await this.readSession(false)
      const stored = session?.tokens?.idToken?.toString()
      if (!stored) {
        this.cachedToken = null
        return stored
      }
      this.rateLimitBackoffMs = INITIAL_BACKOFF_MS
      if (stored !== this.cachedToken?.value) {
        this.cachedToken = {
          value: stored,
          expiresAt: this.tokenExpiry(stored),
          forced: false,
        }
      }
      if (!this.needsRefresh(this.cachedToken)) return stored
      if (Date.now() < this.rateLimitBackoffUntil) return stored
    }
    return this.forceRefresh()
  }

  private recoverFromRefreshError(err) {
    if (isPermanentSessionError(err)) {
      // Permanent — the refresh token can never be used again.
      // Clear the cache (stale token is worthless) and block all
      // further Cognito calls until the user signs in again.
      this.cachedToken = null
      this.revokedTokenUntil = Date.now() + 60 * 60 * 1000 // 1 hour
      throw err
    }
    // Temporary (rate limit, network error, Cognito 5xx) — schedule the next
    // attempt with exponential backoff. An early refresh must not fail calls
    // while the current token is still valid, so serve it; after a rate limit
    // serve even a stale one, since the backend rejects it only once expired.
    const rateLimited = isTooManyRequestsError(err)
    this.backoffIsRateLimit = rateLimited
    this.rateLimitBackoffUntil = Date.now() + this.rateLimitBackoffMs
    this.rateLimitBackoffMs = Math.min(
      this.rateLimitBackoffMs * 2,
      MAX_BACKOFF_MS
    )
    const live = this.cachedToken && this.cachedToken.expiresAt > Date.now()
    if (this.cachedToken && (rateLimited || live)) return this.cachedToken.value
    throw err
  }

  private async resolveToken(force = false) {
    if (Date.now() < this.revokedTokenUntil) {
      throw new Error('Session revoked — please sign in again')
    }

    // We were recently rate-limited. A forced refresh waits out the window.
    // Without a live cached token, skip the session read too: Amplify would
    // call Cognito to refresh an expired token.
    if (Date.now() < this.rateLimitBackoffUntil) {
      const live = this.cachedToken && this.cachedToken.expiresAt > Date.now()
      if (force || !live) {
        if (!force && this.cachedToken) return this.cachedToken.value
        throw new Error(
          this.backoffIsRateLimit
            ? 'Cognito rate limited — please retry in a moment'
            : 'Cognito unavailable — please retry in a moment'
        )
      }
    }

    // Share an in-flight read instead of firing another request to Cognito
    // (prevents TooManyRequestsException when several API calls arrive at
    // once). Forced callers share only an in-flight forced refresh.
    if (this.pendingTokenFetch && (!force || this.pendingIsForced)) {
      return this.pendingTokenFetch
    }

    const promise = this.fetchToken(force)
      .catch(err => this.recoverFromRefreshError(err))
      .finally(() => {
        if (this.pendingTokenFetch === promise) {
          this.pendingTokenFetch = null
          this.pendingIsForced = false
        }
      })
    this.pendingTokenFetch = promise
    this.pendingIsForced = force
    return promise
  }

  private isUnauthorized(err) {
    return (
      err?.statusCode === 401 ||
      err?.status === 401 ||
      err?.response?.status === 401
    )
  }

  // Retries a 401 once with a fresh Cognito token. Requests that bring their
  // own tokenGenerator (session temp tokens) are not Cognito calls and are
  // never retried. If the refresh fails or yields the same token, the caller
  // gets the original 401.
  private async withUnauthorizedRetry(run, config) {
    const token = await this.getToken(config)
    try {
      return await run(token)
    } catch (err) {
      if (config?.tokenGenerator || !this.isUnauthorized(err)) throw err
      let next
      try {
        next = await this.resolveToken(true)
      } catch {
        throw err
      }
      if (!next || next === token) throw err
      return run(next)
    }
  }

  async get<Type>(url: string, params = {}, meta: any = {}) {
    const run = token =>
      this.backendInstance.get<Type>(url, {
        headers: {
          Authorization: 'Bearer ' + token,
          ...this.getAppMode(),
        },
        ...params,
        ...meta,
      })
    return this.withUnauthorizedRetry(run, meta)
  }

  async post<Type>(url: string, data = {}, config?: any) {
    const resolvedConfig = { headers: {}, ...config }
    const run = token =>
      this.backendInstance.post<Type>(url, data, {
        ...resolvedConfig,
        headers: {
          ...resolvedConfig?.headers,
          Authorization: 'Bearer ' + token,
          ...this.getAppMode(),
        },
      })
    return this.withUnauthorizedRetry(run, resolvedConfig)
  }

  getAppMode() {
    // @ts-ignore
    const mode = globalThis.__mode
    if (mode === APP_MODES.UNITY) return { 'x-app': mode }
    return {}
  }

  private async getToken(config: any) {
    const { tokenGenerator } = config
    const token: string = await (tokenGenerator?.() || this.tokenGenerator())
    return token
  }

  setAmplify(amplify) {
    const nextKey = this.sessionKey(amplify)
    this.amplify = amplify
    // AuthProvider calls this on every render. Reset only when the signed-in
    // user changes so an in-memory token can outlive a re-render.
    if (nextKey === this.amplifySessionKey) return
    this.amplifySessionKey = nextKey
    this.cachedToken = null
    this.pendingTokenFetch = null
    this.pendingIsForced = false
    this.rateLimitBackoffUntil = 0
    this.rateLimitBackoffMs = INITIAL_BACKOFF_MS
    this.revokedTokenUntil = 0
  }
}

export const apiService = new ApiService(backendInstance)
